import { createFileRoute } from '@tanstack/react-router';
import { useConfirm } from '@/components/DialogProvider';
import { useQuery, useQueryClient, useMutation, useQueries } from '@tanstack/react-query';
import { useMemo, useState, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, CalendarCheck, PencilRuler, SquareX, Eye, EyeOff } from 'lucide-react';
import { apartmentsQuery } from '@/lib/apartments';
import { usePermissions } from '@/lib/me';
import { AccessDenied } from '@/components/RouteError';
import type { Apartment } from '@/lib/apartments';
import { enumerateDates, monthLabelOf, todayYMD, runFullSync, addDaysYMD, addMonthsYMD, firstOfMonthYMD } from '@/lib/calendar';
import type { TriggerFullSyncResponse } from '@/lib/calendar';
import { ratePlansQuery } from '@/lib/rate_plans';
import { previewPricingQuery } from '@/lib/pricing';
import { bulkSetAvailability, bulkSetRestrictions, releaseRestrictionOverrides } from '@/api/generated/sdk.gen';
import BulkEditDrawer from '@/components/BulkEditDrawer';
import type { SelectedCell, RestrictionEdit } from '@/components/BulkEditDrawer';
import CalendarGrid, { type CalendarGridHandle } from '@/components/CalendarGrid';
import { BookingDrawer } from '@/components/BookingDrawer';
import { CalendarBulkEditDialog, CalendarBlockDatesDialog } from '@/components/CalendarBulkActions';
import type { BulkEditParams, BlockDatesParams } from '@/components/CalendarBulkActions';
import CalendarApartmentFilter from '@/components/CalendarApartmentFilter';
import CalendarLegend from '@/components/CalendarLegend';
import { CalendarSelectionBar } from '@/components/CalendarSelectionBar';
import { NewBookingForm } from '@/components/booking/NewBookingForm';
import { Dialog } from '@/components/ui/dialog';
import { bookingGateFromSelection, computeSelectionMeta } from '@/lib/calendarSelection';
import { posthog } from '@/lib/posthog';
import { usePageHeader } from '@/components/PageHeader';
import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/_app/calendar')({
  loader: ({ context }) => context.queryClient.ensureQueryData(apartmentsQuery),
  component: CalendarPage,
});

/** Extract a human-readable message from the hey-api SDK error (raw Problem JSON). */
function parseSyncError(err: unknown): string {
  if (typeof err === 'string') return 'Network error — server unreachable or returned non-JSON.';
  if (err && typeof err === 'object') {
    const p = err as { status?: number; detail?: string; title?: string };
    if (p.detail) return p.detail;
    if (p.status === 403) return 'Insufficient permissions: sync.trigger permission required.';
    if (p.title) return p.title;
  }
  return 'Unknown error';
}

/** True for a Fri/Sat YYYY-MM-DD (the "weekends only" bulk-edit filter). */
function isWeekendYMD(ymd: string): boolean {
  const p = ymd.split('-');
  const dow = new Date(Date.UTC(Number(p[0]), Number(p[1]) - 1, Number(p[2]))).getUTCDay();
  return dow === 5 || dow === 6;
}

function CalendarPage() {
  const queryClient = useQueryClient();
  const { data: apartments } = useQuery(apartmentsQuery);
  const askConfirm = useConfirm();
  const { toast } = useToast();
  const { can } = usePermissions();

  // The grid owns the infinite date ribbon + month-chunked fetch; the toolbar
  // drives it imperatively (◀/▶/Today) and shows the month it reports back.
  const gridRef = useRef<CalendarGridHandle>(null);
  const [monthLabel, setMonthLabel] = useState(() => monthLabelOf(todayYMD()));
  // Clicking a booking bar opens the quick-view drawer.
  const [openBookingId, setOpenBookingId] = useState<string | null>(null);
  // Toolbar bulk actions (design calendar.jsx): from-scratch Bulk Edit + Block Dates.
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [blockDatesOpen, setBlockDatesOpen] = useState(false);

  // --- Full-sync mutation ---
  const fullSync = useMutation<TriggerFullSyncResponse, unknown>({
    mutationFn: runFullSync,
    onSuccess: (data) => {
      toast({
        kind: 'success',
        title: 'Full sync complete',
        message: `${data.availability_rows} availability + ${data.restriction_rows} restriction rows (${data.from} – ${data.to}).`,
      });
      queryClient.invalidateQueries({ queryKey: ['ari', 'calendar'] }).catch(() => undefined);
    },
    onError: (err) => {
      console.error('full-sync mutation failed:', err);
      toast({ kind: 'error', title: 'Full sync failed', message: parseSyncError(err) });
    },
  });

  const handleFullSync = useCallback(async () => {
    if (fullSync.isPending) return;
    const ok = await askConfirm({
      title: 'Run full ARI sync?',
      body: 'This pushes today → +24 months to the channel manager via the outbox.',
      confirmLabel: 'Run sync',
    });
    if (!ok) return;
    posthog.capture?.('pms_action', { action: 'ari_full_sync_triggered' });
    fullSync.mutate();
  }, [fullSync, askConfirm]);

  // Apartment filter: null = "all" (default); a Set restricts grid rows + fetch scope.
  const allAptIds = useMemo(() => (apartments ?? []).map((a: Apartment) => a.id), [apartments]);
  const [selectedAptIds, setSelectedAptIds] = useState<Set<string> | null>(null);
  const effectiveSelected = useMemo(() => selectedAptIds ?? new Set(allAptIds), [selectedAptIds, allAptIds]);
  const apartmentIds = useMemo(() => [...effectiveSelected].sort(), [effectiveSelected]);

  const gridApartments = useMemo(
    () =>
      (apartments ?? [])
        .filter((apt: Apartment) => effectiveSelected.has(apt.id))
        .map((apt: Apartment) => ({
          id: apt.id,
          title: apt.title,
          code: apt.code,
          sleeps: apt.occupancy_max,
          nickname: apt.nickname,
        })),
    [apartments, effectiveSelected],
  );

  const allFilterApts = useMemo(
    () => (apartments ?? []).map((a: Apartment) => ({ id: a.id, title: a.title })),
    [apartments],
  );

  // Rate plans — only the restriction-WRITE path needs them (the calendar read
  // returns restrictions pre-mapped to apartment_id); translate on apply.
  const { data: ratePlansData } = useQuery(ratePlansQuery(apartmentIds));
  const apartmentToRatePlan = useMemo(() => {
    const m = new Map<string, string>();
    for (const rp of ratePlansData ?? []) if (rp.is_active && !m.has(rp.apartment_id)) m.set(rp.apartment_id, rp.id);
    for (const rp of ratePlansData ?? []) if (!m.has(rp.apartment_id)) m.set(rp.apartment_id, rp.id);
    return m;
  }, [ratePlansData]);

  // --- Strategy preview overlay (roadmap §1r slice 5d) ---
  // Off by default. ON: overlay each visible, uncovered cell with the rate the
  // apartment's pricing STRATEGY would compute via POST /pricing/preview, so an
  // operator can compare it against the currently pushed rate.
  const [previewMode, setPreviewMode] = useState(false);

  // The grid reports its exact rendered date span (CalendarGrid's own ARI fetch
  // window) via onVisibleRangeChange; we snap that to whole-month bounds so the
  // preview query key (and the React Query cache) stays stable while scrolling
  // within a month, and so the snapped window always fully covers — never
  // straddles past the edge of — the rendered span. NOT the whole virtual
  // ribbon: only ever 1-2 months around what's on screen.
  const [previewWindow, setPreviewWindow] = useState<{ from: string; to: string }>(() => {
    const from = firstOfMonthYMD(todayYMD());
    return { from, to: addDaysYMD(addMonthsYMD(from, 1), -1) };
  });
  const handleVisibleRangeChange = useCallback((from: string, toExcl: string) => {
    const toInclusive = addDaysYMD(toExcl, -1);
    const snappedFrom = firstOfMonthYMD(from);
    const snappedTo = addDaysYMD(addMonthsYMD(firstOfMonthYMD(toInclusive), 1), -1);
    setPreviewWindow((prev) => (prev.from === snappedFrom && prev.to === snappedTo ? prev : { from: snappedFrom, to: snappedTo }));
  }, []);

  // One preview fetch per visible apartment for the snapped window — never
  // per-cell, never the whole ribbon. Only runs while the toggle is ON; an
  // apartment with no pricing settings 404s (mapped to [] by previewPricingQuery,
  // i.e. "no overlay for this apartment"), not an error.
  const previewQueries = useQueries({
    queries: apartmentIds.map((aptId) => ({
      ...previewPricingQuery(aptId, previewWindow.from, previewWindow.to),
      // AND the toggle with the helper's own truthiness guard rather than
      // replacing it, so an empty apartment id / window can never fire a request.
      enabled: previewMode && Boolean(aptId) && Boolean(previewWindow.from) && Boolean(previewWindow.to),
    })),
  });
  const previewFingerprint = previewQueries.map((q) => q.dataUpdatedAt ?? 0).join(',');
  const previewRateByCell = useMemo(() => {
    const m = new Map<string, number>();
    if (!previewMode) return m;
    apartmentIds.forEach((aptId, i) => {
      for (const cell of previewQueries[i]?.data ?? []) m.set(`${aptId}:${cell.date}`, cell.rate_minor);
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMode, apartmentIds, previewFingerprint]);

  // --- Selection → action bar → (bulk edit | create booking) ---
  // A selection first shows the CalendarSelectionBar; the operator then opens
  // either the bulk-edit drawer or the create-booking drawer. New selections
  // always start back at the bar.
  const [selectedCells, setSelectedCells] = useState<SelectedCell[] | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const handleRangeSelected = useCallback((cells: SelectedCell[]) => {
    setSelectedCells(cells);
    setBulkOpen(false);
    setBookingOpen(false);
  }, []);
  const finishSelection = useCallback(() => {
    setSelectedCells(null);
    setBulkOpen(false);
    setBookingOpen(false);
  }, []);
  const handleDrawerCancel = useCallback(() => setBulkOpen(false), []);

  // Selection metadata for the bar + create-booking prefill (days/nights,
  // single-apt, label, checkin/checkout). Extracted + unit-tested in
  // lib/calendarSelection.
  const selectionMeta = useMemo(
    () => computeSelectionMeta(selectedCells ?? [], apartments ?? []),
    [selectedCells, apartments],
  );
  // Whether that selection can become a booking — one apartment, a real period,
  // and the permission. Same module, same tests (OH-140).
  const bookingGate = useMemo(
    () => bookingGateFromSelection(selectionMeta, can('bookings.write')),
    [selectionMeta, can],
  );

  const handleDrawerApply = useCallback(
    async (units: number) => {
      if (!selectedCells) return;
      await bulkSetAvailability({
        throwOnError: true,
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body: { items: selectedCells.map((c) => ({ apartment_id: c.apartmentId, date: c.date, units })) },
      });
      const cellCount = selectedCells.length;
      finishSelection();
      posthog.capture?.('pms_action', { action: 'ari_bulk_set_availability', cells: cellCount });
      toast({ kind: 'success', title: 'Availability updated', message: `${cellCount} cell${cellCount === 1 ? '' : 's'}` });
      queryClient.invalidateQueries({ queryKey: ['ari', 'calendar'] }).catch(() => undefined);
    },
    [selectedCells, queryClient, toast, finishSelection],
  );

  const handleRestrictionsApply = useCallback(
    async (edit: RestrictionEdit) => {
      if (!selectedCells) return;
      const apartmentIdsSel = [...new Set(selectedCells.map((c) => c.apartmentId))];
      const dates = enumerateDates(edit.dateFrom, edit.dateTo);
      const missing: string[] = [];
      const items = [];
      for (const aptId of apartmentIdsSel) {
        const ratePlanId = apartmentToRatePlan.get(aptId);
        if (!ratePlanId) {
          missing.push(aptId);
          continue;
        }
        for (const date of dates) {
          items.push({
            rate_plan_id: ratePlanId,
            date,
            rate_minor_units: edit.rateMinorUnits,
            min_stay_through: edit.minStayThrough,
            min_stay_arrival: edit.minStayArrival,
            max_stay: edit.maxStay,
            closed_to_arrival: edit.closedToArrival,
            closed_to_departure: edit.closedToDeparture,
            stop_sell: edit.stopSell,
          });
        }
      }
      if (missing.length > 0) {
        throw new Error(`No rate plan found for ${missing.length} selected apartment(s); nothing was changed.`);
      }
      if (items.length === 0) throw new Error('No rate plan found for the selected apartment(s).');
      if (items.length > 5000) {
        throw new Error(`Too many cells (${items.length}, max 5000). Narrow the date range or select fewer apartments.`);
      }
      await bulkSetRestrictions({
        throwOnError: true,
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body: { items },
      });
      finishSelection();
      posthog.capture?.('pms_action', {
        action: 'ari_bulk_set_restrictions',
        apartments: apartmentIdsSel.length,
        dates: dates.length,
      });
      toast({
        kind: 'success',
        title: 'Rates & restrictions updated',
        message: `${apartmentIdsSel.length} apartment(s) · ${dates.length} date(s)`,
      });
      queryClient.invalidateQueries({ queryKey: ['ari', 'calendar'] }).catch(() => undefined);
    },
    [selectedCells, apartmentToRatePlan, queryClient, toast, finishSelection],
  );

  // "Return to strategy" — clear ALL operator pins (overridden_fields) over the
  // selected apartments × date range so the pricing engine manages them again.
  // One release call per apartment (the endpoint is range-scoped per rate plan).
  const handleReturnToStrategy = useCallback(
    async ({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }) => {
      if (!selectedCells) return;
      const apartmentIdsSel = [...new Set(selectedCells.map((c) => c.apartmentId))];
      const resolved: { aptId: string; ratePlanId: string }[] = [];
      const missing: string[] = [];
      for (const aptId of apartmentIdsSel) {
        const ratePlanId = apartmentToRatePlan.get(aptId);
        if (ratePlanId) resolved.push({ aptId, ratePlanId });
        else missing.push(aptId);
      }
      // Mirror handleRestrictionsApply: fail the whole action if ANY apartment
      // lacks a rate plan (nothing released) rather than silently skipping it.
      if (missing.length > 0) {
        throw new Error(`No rate plan found for ${missing.length} selected apartment(s); nothing was changed.`);
      }
      let released = 0;
      try {
        for (const { aptId, ratePlanId } of resolved) {
          const res = await releaseRestrictionOverrides({
            throwOnError: true,
            body: { apartment_id: aptId, rate_plan_id: ratePlanId, date_from: dateFrom, date_to: dateTo, fields: [] },
          });
          released += res.data?.released_count ?? 0;
        }
      } finally {
        // Refresh even on partial failure so already-released cells update.
        queryClient.invalidateQueries({ queryKey: ['ari', 'calendar'] }).catch(() => undefined);
      }
      finishSelection();
      toast({
        kind: 'success',
        title: 'Returned to strategy',
        message: `${released} pinned ${released === 1 ? 'cell' : 'cells'} cleared`,
      });
    },
    [selectedCells, apartmentToRatePlan, queryClient, toast, finishSelection],
  );

  // --- Toolbar Bulk Edit (from-scratch: apartments × window → rates/availability) ---
  const handleBulkEditApply = useCallback(
    async (p: BulkEditParams) => {
      // Two non-atomic writes below (rates then availability). Track the first so
      // a failure on the second reports honestly and still refreshes the grid.
      let ratesApplied = false;  
      try {
        let dates = enumerateDates(p.from, p.to);
        if (p.weekendsOnly) dates = dates.filter(isWeekendYMD);
        if (dates.length === 0) throw new Error('No matching nights in the selected window.');

        if (p.rateMinorUnits !== undefined) {
          const rate = p.rateMinorUnits;
          const missing: string[] = [];
          const items = [];
          for (const aptId of p.apartmentIds) {
            const ratePlanId = apartmentToRatePlan.get(aptId);
            if (!ratePlanId) {
              missing.push(aptId);
              continue;
            }
            for (const date of dates) {
              items.push({
                rate_plan_id: ratePlanId,
                date,
                rate_minor_units: rate,
                min_stay_through: undefined,
                min_stay_arrival: undefined,
                max_stay: undefined,
                closed_to_arrival: undefined,
                closed_to_departure: undefined,
                stop_sell: undefined,
              });
            }
          }
          if (missing.length > 0) throw new Error(`No rate plan found for ${missing.length} apartment(s); nothing was changed.`);
          if (items.length === 0) throw new Error('No rate plan found for the selected apartment(s).');
          if (items.length > 5000) throw new Error(`Too many cells (${items.length}, max 5000). Narrow the window or apartments.`);
          await bulkSetRestrictions({ throwOnError: true, headers: { 'Idempotency-Key': crypto.randomUUID() }, body: { items } });
          ratesApplied = true;
        }

        if (p.availabilityUnits !== undefined) {
          const units = p.availabilityUnits;
          const cells = p.apartmentIds.flatMap((aptId) => dates.map((date) => ({ apartment_id: aptId, date, units })));
          if (cells.length > 5000) throw new Error(`Too many cells (${cells.length}, max 5000). Narrow the window or apartments.`);
          await bulkSetAvailability({ throwOnError: true, headers: { 'Idempotency-Key': crypto.randomUUID() }, body: { items: cells } });
        }

        setBulkEditOpen(false);
        posthog.capture?.('pms_action', { action: 'ari_bulk_edit', apartments: p.apartmentIds.length, dates: dates.length });
        toast({ kind: 'success', title: 'Bulk update applied', message: `${p.apartmentIds.length} apartment(s) · ${dates.length} night(s)` });
        queryClient.invalidateQueries({ queryKey: ['ari', 'calendar'] }).catch(() => undefined);
      } catch (err) {
        if (ratesApplied) {
          // Rates committed but the availability write failed: refresh so the grid
          // reflects the saved rates, and tell the operator exactly what to retry.
          queryClient.invalidateQueries({ queryKey: ['ari', 'calendar'] }).catch(() => undefined);
          toast({
            kind: 'error',
            title: 'Bulk edit partially applied',
            message: 'Rates were saved, but the availability update failed — reopen Bulk Edit to retry availability.',
          });
        } else {
          toast({ kind: 'error', title: 'Bulk edit failed', message: parseSyncError(err) });
        }
      }
    },
    [apartmentToRatePlan, queryClient, toast],
  );

  // --- Toolbar Block Dates (apartments × window → availability stop-sell/0) ---
  const handleBlockDatesApply = useCallback(
    async (p: BlockDatesParams) => {
      try {
        const dates = enumerateDates(p.from, p.to);
        if (dates.length === 0) throw new Error('No nights in the selected window.');
        const cells = p.apartmentIds.flatMap((aptId) => dates.map((date) => ({ apartment_id: aptId, date, units: 0 })));
        if (cells.length > 5000) throw new Error(`Too many cells (${cells.length}, max 5000). Narrow the window or apartments.`);
        await bulkSetAvailability({ throwOnError: true, headers: { 'Idempotency-Key': crypto.randomUUID() }, body: { items: cells } });
        setBlockDatesOpen(false);
        posthog.capture?.('pms_action', { action: 'ari_block_dates', apartments: p.apartmentIds.length, dates: dates.length });
        toast({ kind: 'success', title: 'Dates blocked', message: `${p.apartmentIds.length} apartment(s) · ${dates.length} night(s)` });
        queryClient.invalidateQueries({ queryKey: ['ari', 'calendar'] }).catch(() => undefined);
      } catch (err) {
        toast({ kind: 'error', title: 'Block dates failed', message: parseSyncError(err) });
      }
    },
    [queryClient, toast],
  );

  usePageHeader({
    subtitle: fullSync.isPending ? 'Running full sync…' : undefined,
    actions: can('sync.trigger') ? (
      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-label="Run full ARI sync"
        loading={fullSync.isPending}
        disabled={fullSync.isPending}
        onClick={handleFullSync}
      >
        Run full sync
      </Button>
    ) : undefined,
  });

  // The calendar backend returns EMPTY (not 403) without availability.read —
  // an explicit gate beats a silently blank grid on direct-URL access.
  if (!can('availability.read')) {
    return <AccessDenied />;
  }

  // The backend withholds the bookings/holds overlay without bookings.read —
  // say so, or "no booking bars" is indistinguishable from "no bookings".
  const overlayWithheld = !can('bookings.read');

  if (!apartments || apartments.length === 0) {
    return (
      <div className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card px-5 py-8 text-center text-sm text-muted">
        No apartments to display. Seed apartments first.
      </div>
    );
  }

  return (
    <div className="max-w-full">
      {overlayWithheld && (
        <p className="mb-3 rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-card px-3 py-2 text-xs text-muted">
          Booking overlay hidden — your role lacks the bookings permission.
        </p>
      )}
      {/* Toolbar: month ribbon nav (left) + filter / legend (right) */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {/* Apartment filter on the LEFT so its left-anchored panel stays on-page. */}
          <CalendarApartmentFilter apartments={allFilterApts} selected={effectiveSelected} onChange={setSelectedAptIds} />
          <span className="mx-1 h-5 w-px bg-[var(--border-soft)]" aria-hidden="true" />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label="Previous month"
            onClick={() => gridRef.current?.stepMonth(-1)}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <span className="min-w-[140px] text-center text-base font-bold text-[var(--text-heading)]">{monthLabel}</span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label="Next month"
            onClick={() => gridRef.current?.stepMonth(1)}
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => gridRef.current?.goToday()}>
            <CalendarCheck className="mr-1 h-4 w-4" aria-hidden="true" />
            Today
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <CalendarLegend />
          <span className="mx-1 h-5 w-px bg-[var(--border-soft)]" aria-hidden="true" />
          <Button
            type="button"
            variant={previewMode ? 'gold' : 'secondary'}
            size="sm"
            aria-pressed={previewMode}
            aria-label={previewMode ? 'Hide strategy preview overlay' : 'Show strategy preview overlay'}
            onClick={() => setPreviewMode((v) => !v)}
          >
            {previewMode ? (
              <Eye className="mr-1 h-4 w-4" aria-hidden="true" />
            ) : (
              <EyeOff className="mr-1 h-4 w-4" aria-hidden="true" />
            )}
            Strategy preview
          </Button>
          {can('availability.write') && (
            <>
              <span className="mx-1 h-5 w-px bg-[var(--border-soft)]" aria-hidden="true" />
              <Button type="button" variant="secondary" size="sm" onClick={() => setBulkEditOpen(true)}>
                <PencilRuler className="mr-1 h-4 w-4" aria-hidden="true" />
                Bulk Edit
              </Button>
              <Button type="button" variant="primary" size="sm" onClick={() => setBlockDatesOpen(true)}>
                <SquareX className="mr-1 h-4 w-4" aria-hidden="true" />
                Block Dates
              </Button>
            </>
          )}
        </div>
      </div>

      <CalendarGrid
        ref={gridRef}
        apartments={gridApartments}
        apartmentIds={apartmentIds}
        onRangeSelected={handleRangeSelected}
        onMonthChange={setMonthLabel}
        onVisibleRangeChange={handleVisibleRangeChange}
        onBookingClick={setOpenBookingId}
        previewMode={previewMode}
        previewRateByCell={previewRateByCell}
      />

      {selectionMeta && !bulkOpen && !bookingOpen && (
        <CalendarSelectionBar
          days={selectionMeta.days}
          checkinDate={selectionMeta.checkinDate}
          checkoutDate={selectionMeta.checkoutDate}
          apartmentLabel={selectionMeta.apartmentLabel}
          canCreateBooking={bookingGate.allowed}
          disabledReason={bookingGate.reason}
          onBulkEdit={() => setBulkOpen(true)}
          onCreateBooking={() => setBookingOpen(true)}
          onDismiss={finishSelection}
        />
      )}

      <BulkEditDrawer
        open={bulkOpen}
        cells={selectedCells ?? []}
        onCancel={handleDrawerCancel}
        onApplyAvailability={handleDrawerApply}
        onApplyRestrictions={handleRestrictionsApply}
        onReturnToStrategy={handleReturnToStrategy}
      />

      <Dialog
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        title="Create booking"
        description="Offline / walk-in booking over the selected dates."
        side
      >
        {bookingOpen && selectionMeta && (
          <NewBookingForm
            // Cancel/X/Escape/backdrop all return to the selection bar (consistent
            // with BulkEditDrawer); the bar's own dismiss clears the selection. A
            // successful create navigates away, unmounting the calendar.
            onClose={() => setBookingOpen(false)}
            prefill={{
              apartmentId: selectionMeta.apartmentId,
              checkin: selectionMeta.checkinDate,
              checkout: selectionMeta.checkoutDate,
            }}
          />
        )}
      </Dialog>

      <BookingDrawer bookingId={openBookingId} onClose={() => setOpenBookingId(null)} />

      {bulkEditOpen && (
        <CalendarBulkEditDialog
          apartments={gridApartments}
          onCancel={() => setBulkEditOpen(false)}
          onApply={handleBulkEditApply}
        />
      )}
      {blockDatesOpen && (
        <CalendarBlockDatesDialog
          apartments={gridApartments}
          onCancel={() => setBlockDatesOpen(false)}
          onApply={handleBlockDatesApply}
        />
      )}
    </div>
  );
}
